import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  InputBase,
  makeStyles,
  Slider,
  Fade,
  Paper,
  InputAdornment,
  Switch,
} from '@material-ui/core';
import { images } from 'assets/images';
import { HvButton, HvSelect } from 'components';
import { MAX_FILTER_PRICE, COLOR } from 'config/constants';
import { useIntl } from 'react-intl';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { isMobile } from 'utils/screen';

const useStyles = makeStyles({
  filterButton: {
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: '2px solid #F5F5F5',
    width: 60,
    cursor: 'pointer',
    height: 50,
    '@media (max-width: 400px)': {
      width: 50,
    },
  },
  filterContainer: {
    display: 'flex',
    '@media (max-width: 650px)': {
      border: '2px solid #F5F5F5',
    },
  },
  filterButtonIcon: {
    width: 40,
    height: 40,
  },
  selectedFilterButton: {
    backgroundColor: '#00BDF2',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: '2px solid #F5F5F5',
    width: 60,
    cursor: 'pointer',
    height: 50,
    '@media (max-width: 400px)': {
      width: 50,
    },
  },
  searchBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    backgroundColor: COLOR.WHITE,
    minWidth: 300,
    height: 50,
    border: `1px solid ${COLOR.GREY}`,
    borderRadius: 8,
    '@media (max-width: 650px)': {
      flexDirection: 'column',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      backgroundColor: 'white',
      height: 'auto',
      border: 'none',
    },
  },
  searchInput: {
    paddingLeft: 10,
    backgroundColor: COLOR.WHITE,
    height: 50,
    width: '100%',
    borderRadius: 8,
    '@media (max-width: 650px)': {
      height: 50,
      marginBottom: 10,
      border: `1px solid ${COLOR.GREY}`,
    },
  },
  searchButton: {
    color: 'white',
    borderRadius: '0 5px 5px 0',
    height: 50,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
    '@media (max-width: 650px)': {
      display: 'none',
    },
  },
  clearSearchIcon: {
    color: 'red',
    marginRight: 10,
    cursor: 'pointer',
    width: 20,
    height: 20,
  },
  clearSearchButton: {
    color: 'white',
    backgroundColor: '#FF2E2E',
    marginBottom: 10,
    '&:hover': {
      backgroundColor: '#D10000',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  iconsRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  control: {
    flex: '1 1',
    minWidth: 130,
    marginRight: 10,

    '&:last-child': {
      marginBottom: 0,
    },
  },
  selectDropdown: {
    overflow: 'hidden',
    fontSize: 12,
  },
  filter_icon: {
    height: 25,
    width: 25,
    marginRight: 10,
  },
  fadeContent: {
    marginRight: 10,
  },
  priceLabel: {
    marginRight: 10,
    '@media (max-width: 600px)': {
      width: '100%',
      margin: 'auto',
    },
  },
  priceContent: {
    minWidth: 400,
    height: 100,
    zIndex: 9999,
    position: 'absolute',
    right: 50,
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    '@media (max-width: 800px)': {
      right: 'auto',
      height: 150,
      minWidth: '80%',
      width: '80%',
    },
  },
  slider: {
    '& .MuiSlider-valueLabel': {
      '&:before': {
        display: 'none',
      },
      '& *': {
        background: 'transparent',
        color: '#000',
      },
    },
  },
  filterIconsRow: {
    display: 'flex',
    flexDirection: 'row',
    '@media (max-width: 768px)': {
      marginTop: 10,
    },
  },
  filterIconRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '@media (max-width: 600px)': {
      marginBottom: 10,
    },
  },
  switchContainer: {
    marginRight: 15,
    borderRadius: 40,
    width: '100%',
    height: 40,
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 5,
    paddingRight: 5,
  },
  switchButton: {
    cursor: 'pointer',
    paddingLeft: 5,
    paddingRight: 5,
    borderRadius: 30,
    height: 30,
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  selectedSwitchButton: {
    cursor: 'pointer',
    paddingLeft: 5,
    paddingRight: 5,
    backgroundColor: COLOR.WHITE,
    borderRadius: 30,
    height: 30,
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
  },
});

interface Props {
  centris_filters: any;
  setCentrisFilters: (filters: any) => void;
  setCentrisSearchResults: (searchResults: []) => void;
  handleCentrisListingSearch: (search_input: string) => void;
  style?: any;
  secondary_bg_text_color?: string;
}

function CentrisSearchBar(props: Props) {
  const {
    setCentrisSearchResults,
    centris_filters,
    setCentrisFilters,
    handleCentrisListingSearch,
    style,
    secondary_bg_text_color,
  } = props;
  const styles = useStyles();
  const intl = useIntl();
  const [showPrice, setShowPrice] = useState<boolean>(false);
  const [search_input, setSearchInput] = useState<string>('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [property_type, setPropertyType] = useState('buy');

  const fmtSearchListing = intl.formatMessage({ id: 'searchListing' });
  const fmtClearSearch = intl.formatMessage({ id: 'portal.clearSearch' });
  const fmtSearchByPrice = intl.formatMessage({ id: 'portal.searchByPrice' });
  const fmtBedrooms = intl.formatMessage({ id: 'portal.bedrooms' });
  const fmtBathrooms = intl.formatMessage({ id: 'portal.bathrooms' });
  const fmtPrice = intl.formatMessage({ id: 'portal.price' });
  const fmtClose = intl.formatMessage({ id: 'portal.close' });
  const fmtBuy = intl.formatMessage({ id: 'portal.buy' });
  const fmtRent = intl.formatMessage({ id: 'portal.rent' });

  const primary_color = style?.primary_color || COLOR.LIGHT_BLUE;
  const secondary_color = style?.secondary_color || COLOR.LIGHT_BLUE;
  const bedroomOptions = [
    { value: 0, label: fmtBedrooms },
    { value: 1, label: '1+' },
    { value: 2, label: '2+' },
    { value: 3, label: '3+' },
    { value: 4, label: '4+' },
    { value: 5, label: '5+' },
    { value: 6, label: '6+' },
    { value: 7, label: '7+' },
    { value: 8, label: '8+' },
    { value: 9, label: '9+' },
    { value: 10, label: '10+' },
  ];
  const bathroomOptions = [
    { value: 0, label: fmtBathrooms },
    { value: 1, label: '1+' },
    { value: 2, label: '2+' },
    { value: 3, label: '3+' },
    { value: 4, label: '4+' },
    { value: 5, label: '5+' },
  ];
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  });
  const onClickSwitchButton = (type: string) => {
    setPropertyType(type);
    setCentrisFilters({ ...centris_filters, property_type: type });
  };
  const handlePriceChange = (event: any, newValue: number | number[]) => {
    setCentrisFilters({ ...centris_filters, price_range: newValue });
  };
  const onBedroomCountChange = (value: any) => {
    setShowPrice(false);
    setCentrisFilters({ ...centris_filters, min_bedrooms: value });
  };
  const onBathroomCountChange = (value: any) => {
    setShowPrice(false);
    setCentrisFilters({ ...centris_filters, min_bathrooms: value });
  };
  const getPriceText = (value: number) => {
    const options = {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    };
    const plus_sign = value === MAX_FILTER_PRICE ? '+' : '';
    return `${value.toLocaleString('en-CA', options) + plus_sign}`;
  };
  const onClickPrice = () => {
    setShowPrice(!showPrice);
  };
  return (
    <Box className={styles.content}>
      <Box className={styles.searchBar}>
        <InputBase
          className={styles.searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            handleCentrisListingSearch(e.target.value);
          }}
          endAdornment={
            search_input !== '' && (
              <InputAdornment position='end'>
                <CloseIcon
                  className={styles.clearSearchIcon}
                  onClick={() => {
                    setSearchInput('');
                    handleCentrisListingSearch('');
                  }}
                />
              </InputAdornment>
            )
          }
          placeholder={fmtSearchListing}
          value={search_input}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleCentrisListingSearch(search_input);
            }
          }}
        />
        <Box className={styles.iconsRow}>
          <div className={styles.switchContainer}>
            <div
              className={
                property_type === 'buy' ? styles.selectedSwitchButton : styles.switchButton
              }
              onClick={() => {
                return onClickSwitchButton('buy');
              }}
            >
              <Typography style={{ color: secondary_color, fontFamily: 'FordAntenna-Medium' }}>
                {fmtBuy}
              </Typography>
            </div>
            <div
              className={
                property_type === 'rent' ? styles.selectedSwitchButton : styles.switchButton
              }
              onClick={() => {
                return onClickSwitchButton('rent');
              }}
            >
              <Typography style={{ color: secondary_color, fontFamily: 'FordAntenna-Medium' }}>
                {fmtRent}
              </Typography>
            </div>
          </div>
          <div className={styles.filterIconsRow}>
            <Box className={styles.filterIconRow}>
              <img src={images.bedroom_icon} alt='bedroom icon' className={styles.filter_icon} />
              <HvSelect
                classes={{ rootClass: styles.control, dropdownClass: styles.selectDropdown }}
                options={bedroomOptions}
                value={String(centris_filters.min_bedrooms)}
                onChange={onBedroomCountChange}
                onOpen={() => {
                  return setShowPrice(false);
                }}
              />
            </Box>
            <Box className={styles.filterIconRow}>
              <img src={images.bathtub_icon} alt='bathroom icon' className={styles.filter_icon} />
              <HvSelect
                classes={{ rootClass: styles.control, dropdownClass: styles.selectDropdown }}
                options={bathroomOptions}
                value={String(centris_filters.min_bathrooms)}
                onChange={onBathroomCountChange}
                onOpen={() => {
                  return setShowPrice(false);
                }}
              />
            </Box>
          </div>
        </Box>
        <HvButton
          className={styles.priceLabel}
          style={{ backgroundColor: secondary_color, color: secondary_bg_text_color }}
          onClick={onClickPrice}
        >
          <Typography>{fmtPrice}</Typography>
        </HvButton>
        <Button
          variant='contained'
          onClick={() => {
            handleCentrisListingSearch(search_input);
          }}
          className={styles.searchButton}
          style={{ backgroundColor: secondary_color }}
        >
          <SearchIcon />
        </Button>
      </Box>
      <Box className={styles.fadeContent}>
        <Fade in={showPrice}>
          <Paper className={styles.priceContent}>
            <Typography>{fmtSearchByPrice}</Typography>
            <Slider
              className={styles.slider}
              value={centris_filters.price_range}
              onChange={handlePriceChange}
              valueLabelDisplay='off'
              min={0}
              max={MAX_FILTER_PRICE}
              step={1000}
              getAriaValueText={getPriceText}
              valueLabelFormat={getPriceText}
            />
            <Box className={styles.row}>
              <Typography>{getPriceText(centris_filters.price_range[0])}</Typography>
              <Typography>{getPriceText(centris_filters.price_range[1])}</Typography>
            </Box>
            <HvButton
              onClick={() => {
                return setShowPrice(false);
              }}
              style={{ backgroundColor: secondary_color, color: secondary_bg_text_color }}
            >
              <Typography>{fmtClose}</Typography>
            </HvButton>
          </Paper>
        </Fade>
      </Box>
    </Box>
  );
}

export default CentrisSearchBar;
